/**
 * reportUtils — W2.2 + F037
 *
 * Hilfsfunktionen fuer Perioden-Logik im Monatsreport.
 * downloadCsvV2 ausgelagert aus MonthlyReportPage damit react-refresh
 * keine Warnung wirft (nicht-Komponenten-Exports muessen in eigene Datei).
 */
import { activeBcp47, isEnLocale } from '../../lib/locale'
import { ApiError } from '../../lib/errors/ApiError'
import { isDemoMode, getDemoToken } from '../../services/demoMode'

/**
 * Gibt den vorherigen Monat als YYYY-MM-String zurueck.
 * Beispiel: '2026-01' -> '2025-12'
 */
export function prevMonth(period: string): string {
  const [y, m] = period.split('-').map(Number)
  const d = new Date(y, m - 2, 1) // m-2 weil month ist 0-indexed
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/**
 * Gibt den naechsten Monat als YYYY-MM-String zurueck.
 * Beispiel: '2025-12' -> '2026-01'
 */
export function nextMonth(period: string): string {
  const [y, m] = period.split('-').map(Number)
  const d = new Date(y, m, 1) // m (nicht m-1) springt zum naechsten Monat
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/**
 * Prueft ob eine Periode ECHT in der Zukunft liegt (nach dem laufenden Monat).
 *
 * R3/FE-2 (v1-Paritaet): Vorher galt bereits der AKTUELLE Monat als Zukunft
 * (`m >= now.getMonth() + 1`) — dadurch war der laufende Monat ueber die
 * Pfeilnavigation nicht erreichbar. Der v1-Monolith listet den laufenden Monat
 * dagegen IMMER in `User#available_report_months` (auch fuer Free-User).
 */
export function isFuturePeriod(period: string): boolean {
  const [y, m] = period.split('-').map(Number)
  const now = new Date()
  return y > now.getFullYear() || (y === now.getFullYear() && m > now.getMonth() + 1)
}

/**
 * Wandelt eine YYYY-MM-Periode in einen lesbaren Label um.
 * Beispiel: '2026-04' -> 'April 2026'
 */
export function periodLabel(period: string): string {
  const [y, m] = period.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString(activeBcp47(), { month: 'long', year: 'numeric' })
}

/**
 * Label fuer einen Monat aus der BE-Liste (available_months).
 *
 * R3-Review F6: Das BE liefert `label` immer englisch ("May 2026"). Der Wert
 * ("YYYY-MM") wird deshalb clientseitig in der aktiven Sprache formatiert; das
 * BE-Label bleibt nur Fallback fuer unerwartete Werte.
 */
export function monthLabel(value: string, fallback?: string): string {
  if (isValidPeriod(value)) return periodLabel(value)
  return fallback ?? value
}

/**
 * Gibt den Default-Monat des Reports zurueck.
 *
 * R3/FE-1 (v1-Paritaet): Der v1-Monolith (ReportsController#show) und der
 * v2-Endpoint GET /api/v2/reports/available_months verwenden beide den
 * LAUFENDEN Monat als Default (`ReportMonthScope.default_value`). Vorher nahm
 * das FE den Vormonat — fuer Free-User (nur laufender Monat erlaubt) fuehrte
 * das direkt in eine 402-Vollsperre.
 */
export function defaultPeriod(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/**
 * Prueft ob ein Period-String ein gueltiges YYYY-MM-Format hat.
 */
export function isValidPeriod(period: string): boolean {
  return /^\d{4}-\d{2}$/.test(period)
}

/** ISO-4217-Code: exakt 3 Buchstaben (Intl.NumberFormat akzeptiert nichts anderes). */
const ISO_4217_RE = /^[A-Za-z]{3}$/

/**
 * Formatiert einen Fiat-Betrag lokalisiert in der uebergebenen Kontowaehrung
 * (ISO 4217, z.B. "USD", "GBP", "CAD", "TRY"), z.B. 53.31/"USD" -> "$53.31".
 * BE #19: vorher hart auf EUR verdrahtet — 95% der Nutzer haben eine andere
 * Kontowaehrung. `currency` faellt auf "EUR" zurueck (altes Verhalten), falls
 * das BE (noch) keine Waehrung liefert ODER einen nicht-ISO-4217-Code schickt
 * (Intl.NumberFormat wirft sonst RangeError und reisst die ganze Seite runter).
 */
export function formatCurrency(amount: number, currency = 'EUR'): string {
  return new Intl.NumberFormat(activeBcp47(), {
    style: 'currency',
    currency: ISO_4217_RE.test(currency) ? currency : 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Object.is(amount, -0) ? 0 : amount)
}

/**
 * Wartet, bis der Browser mindestens einmal gezeichnet hat.
 *
 * F-R3-2: `window.print()` blockiert den Main-Thread waehrend der (rein
 * clientseitigen) PDF-Erzeugung fuer 15-25s. Wird der Aufruf direkt im
 * Klick-Handler ausgefuehrt, kommt React nicht mehr dazu, den vorher gesetzten
 * Lade-State zu committen — der Nutzer sieht einen eingefrorenen Tab ohne
 * Indikator. Zwei rAF-Ticks (bzw. setTimeout als jsdom-/Fallback-Pfad)
 * garantieren, dass Commit UND Paint durch sind.
 */
export function nextPaint(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame !== 'function') {
      setTimeout(resolve, 0)
      return
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setTimeout(resolve, 0))
    })
  })
}

// --- CSV-Download (F037 / v2-Cutover) ---

export type CsvFormat = 'standard' | 'horizontal' | 'vertical' | 'advanced' | 'koinly'

/** v2-Endpunkt-Pfade — kein Monolith mehr */
const CSV_ENDPOINT_MAP: Record<CsvFormat, string> = {
  standard: '/api/v2/reports/monthly',
  horizontal: '/api/v2/reports/csv',
  vertical: '/api/v2/reports/csv_vertical',
  advanced: '/api/v2/reports/csv_advanced',
  koinly: '/api/v2/reports/koinly',
}

/**
 * Loest den fuer den CSV-Download zu verwendenden Bearer-Token auf.
 *
 * F-R3-1: Der CSV-Download laeuft ueber rohes `fetch` und damit NICHT durch die
 * Auth-Middleware von lib/api-client.ts. Im Demo-Modus ist `accessToken` im
 * authStore `null` — der Download lief deshalb unauthentifiziert und endete in
 * einem 401. Die Aufloesung folgt jetzt derselben Reihenfolge wie
 * `getAuthToken()` im api-client: Demo-Token schlaegt Session-Token.
 */
export function resolveCsvAuthToken(sessionToken: string | null): string | null {
  if (isDemoMode()) return getDemoToken() ?? sessionToken
  return sessionToken
}

/**
 * Fuehrt den fetch aus, wirft bei !ok denselben ApiError-Typ wie der
 * zentrale apiClient (F-R3-1), und loest bei Erfolg einen programmatischen
 * Blob-Download aus (ObjectURL). window.open wuerde keinen
 * Authorization-Header erlauben.
 */
async function fetchAndDownload(
  requestUrl: string,
  headers: HeadersInit,
  fallbackFilename: string,
): Promise<void> {
  const response = await fetch(requestUrl, { headers })

  if (!response.ok) {
    let body: unknown
    try {
      body = await response.clone().json()
    } catch {
      body = await response.clone().text()
    }
    throw new ApiError(response.status, body, response.headers)
  }

  // Dateiname aus Content-Disposition, Fallback auf uebergebenen Namen
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const filenameMatch = /filename="?([^";\s]+)"?/.exec(disposition)
  const filename = filenameMatch?.[1] ?? fallbackFilename

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Laedt eine CSV-Datei per fetch mit Bearer-Auth und loest einen programmatischen
 * Download aus (ObjectURL). window.open wuerde keinen Authorization-Header erlauben.
 */
export async function downloadCsvV2(
  format: CsvFormat,
  period: string,
  sessionToken: string | null,
  contactId?: number,
  timeZone?: string,
): Promise<void> {
  const token = resolveCsvAuthToken(sessionToken)
  const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
  const headers: HeadersInit = {
    Accept: 'text/csv',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }

  // "standard" (Fleet & Tax Monthly Statement) ist ein eigener, bereits
  // bestehender api/v2-Endpoint mit Jahr/Monat im Pfad statt als
  // month-Query-Param — kein contact_id/time_zone, dafuer optional locale.
  let requestUrl: string
  if (format === 'standard') {
    const [year, month] = period.split('-')
    const params = new URLSearchParams({ locale: isEnLocale() ? 'en' : 'de' })
    requestUrl = `${baseUrl}${CSV_ENDPOINT_MAP.standard}/${year}/${month}.csv?${params.toString()}`
  } else {
    const params = new URLSearchParams({ month: period })
    if (contactId !== undefined) params.set('contact_id', String(contactId))
    if (timeZone) params.set('time_zone', timeZone)
    requestUrl = `${baseUrl}${CSV_ENDPOINT_MAP[format]}?${params.toString()}`
  }

  await fetchAndDownload(requestUrl, headers, `${format}_${period}.csv`)
}

/**
 * Issue #95 — Steuer-Jahresexport (CSV). Eigener Endpoint mit Jahr statt
 * Monat im Pfad (GET /api/v2/reports/tax_yearly/:year.csv), Premium-Feature
 * (402 ausserhalb des erlaubten Fensters, gleiche Fehlerbehandlung wie
 * downloadCsvV2).
 */
export async function downloadTaxYearlyCsv(
  year: number,
  sessionToken: string | null,
): Promise<void> {
  const token = resolveCsvAuthToken(sessionToken)
  const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
  const headers: HeadersInit = {
    Accept: 'text/csv',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
  const params = new URLSearchParams({ locale: isEnLocale() ? 'en' : 'de' })
  const requestUrl = `${baseUrl}/api/v2/reports/tax_yearly/${year}.csv?${params.toString()}`

  await fetchAndDownload(requestUrl, headers, `tax_yearly_${year}.csv`)
}
